'use client'

import * as React from 'react'
import createCache from '@emotion/cache'
import {useServerInsertedHTML} from 'next/navigation'
import {CacheProvider} from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import {ThemeProvider} from '@mui/material/styles'
import theme from '../theme'

// This function is updated for Material-UI v6 compatibility
export default function ThemeRegistry({children}: {children: React.ReactNode}) {
    // Use 'css' as emotion key to match existing site configuration
    const options = {key: 'css', prepend: true}

    // Create a singleton cache instance on the client and per request on the server
    const [{cache, flush}] = React.useState(() => {
        const cache = createCache(options)

        // Important for SSR compatibility
        cache.compat = true

        // Track inserted styles to collect them later
        let inserted: string[] = []
        const originalInsert = cache.insert

        // Override insert to track the styles that need to be flushed
        cache.insert = (...args) => {
            const serialized = args[1]
            if (cache.inserted[serialized.name] === undefined) {
                inserted.push(serialized.name)
            }
            return originalInsert(...args)
        }

        // Function to collect and clear the inserted styles
        const flush = () => {
            const prevInserted = inserted
            inserted = []
            return prevInserted
        }

        return {cache, flush}
    })

    // This hook will insert the collected styles into the HTML during SSR
    useServerInsertedHTML(() => {
        const names = flush()
        if (names.length === 0) return null

        // Consolidate all styles for this rendering
        let styles = ''
        for (const name of names) {
            styles += cache.inserted[name]
        }

        // Create a style element with all the collected CSS
        return (
            <style
                key={options.key}
                data-emotion={`${options.key} ${names.join(' ')}`}
                dangerouslySetInnerHTML={{__html: styles}}
            />
        )
    })

    // Provide the emotion cache and theme to the app
    return (
        <CacheProvider value={cache}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </CacheProvider>
    )
}
